/**
 * The fleet link's feature (M6 plan P21): once the career (`m2`, with its write hook) and the strategic client
 * (`strategic`, order 170) are provided, creates the `fleet` service (`src/fleet-link/service.ts`, loaded lazily),
 * which applies every wallet settlement to the Career once and passes the fleet alerts on. M6 P24b: an uninsured
 * flown fleet ship the Career lost on death (`fleet-link/loss.ts`) is sent to the worker as `loseBoardedShip`.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import type { CareerService } from '../../career/flow'
import { logger } from '../../core/logger'
import { pendingBoardedLosses, takeBoardedLosses } from '../../fleet-link/loss'
import type { FleetService } from '../../fleet-link/service'
import type { M2Runtime } from '../../space/contracts'
import strategicJson from '../../data/strategic/strategic.json'

/** After the strategic link (170) and the Charter career bridge (171). */
const ORDER = FEATURE_ORDER.strategic + 2

let service: FleetService | null = null

function hasWriteHook(m2: M2Runtime): m2 is CareerService {
  return 'applyDevCommand' in m2 && typeof m2.applyDevCommand === 'function'
}

const feature: GameFeature = {
  name: 'fleetLink',
  order: ORDER,
  async init(ctx: GameContext): Promise<void> {
    if (ctx.reach === null) return
    const m2 = ctx.services.maybe('m2')
    const client = ctx.services.maybe('strategic')
    if (m2 === undefined || client === undefined || !hasWriteHook(m2)) return
    const [{ createFleetService }, { careerAccess }] = await Promise.all([import('../../fleet-link/service'), import('../../bridge/m2/career')])
    const access = careerAccess(m2)
    service = createFleetService({
      client,
      career: access,
      stockFit: m2.data.career.start.fitted,
      cap: strategicJson.caps.playerShips,
      m2Data: m2.data,
      // M9 CH-7s: a registered claim takes its cockpit hulk out of the local salvage pool.
      despawnHulk: (key) => m2.space.salvage.despawnHulk(key),
      // PG-2 (R2-4): an open claim's cockpit hulk counts outside `hulks.maxLocal`.
      markHulkClaimed: (key, claimed) => void m2.space.salvage.markHulkClaimed(key, claimed),
    })
    ctx.services.provide('fleet', service)
  },
  frame(): void {
    // A settlement the Career refused (no career running yet) is retried here; a no-op while nothing is held.
    if (service !== null && service.settlement.held !== 0) service.settlement.flush()
    // M9 CH-7s: a hulk Career write the worker already acted on (a claimed ship, a scrap) is retried the same way.
    if (service !== null && service.pendingHulkWrites !== 0) service.retryHulkWrites()
    // M6 P24b: free the worker slot of a boarded ship lost uninsured (rare; a no-op while none waits).
    if (service !== null && pendingBoardedLosses() !== 0) {
      for (const shipId of takeBoardedLosses()) {
        void service.run({ type: 'loseBoardedShip', shipId }).then((r) => {
          if (!r.ok) logger.warn(`fleet: loseBoardedShip ${shipId} refused (${r.error})`)
        })
      }
    }
  },
  dispose(): void {
    service?.dispose()
    service = null
  },
}

export default feature
